import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Card, CardHeader, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { trainingService } from '../services/trainingService'
import { getCalendarBadgeData, formatDateTime } from '../utils'
import { Archive, PenSquare, Trash2 } from 'lucide-react'

export const ArchivePage: React.FC = () => {
  // Fetch archived trainings
  const { data: trainingsData, isLoading } = useQuery({
    queryKey: ['trainings', { status: 'past', limit: 50 }],
    queryFn: () => trainingService.getTrainings({ status: 'past', limit: 50 }),
  })

  const trainings = trainingsData?.data?.data || []
  const total = trainingsData?.data?.total || 0

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Träningsarkiv</h1>
          <p className="text-gray-600">Tidigare träningar och närvarohantering</p>
        </div>
        <Link to="/">
          <Button variant="outline">
            Tillbaka till aktiva träningar
          </Button>
        </Link>
      </div>

      <Card>
        <CardHeader>
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Archive className="h-5 w-5 mr-2" />
            Arkiverade Träningar ({total})
          </h3>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gray-200 rounded"></div>
                    <div className="space-y-2">
                      <div className="w-32 h-4 bg-gray-200 rounded"></div>
                      <div className="w-24 h-3 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <div className="w-20 h-8 bg-gray-200 rounded"></div>
                    <div className="w-12 h-8 bg-gray-200 rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : trainings.length === 0 ? (
            <div className="text-center py-12">
              <Archive className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Inga arkiverade träningar</h3>
              <p className="text-gray-500 mb-4">
                Träningar som har passerat flyttas automatiskt till arkivet.
              </p>
              <Link to="/">
                <Button>
                  Tillbaka till aktiva träningar
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {trainings.map((training) => {
                const calendarData = getCalendarBadgeData(training.datum)
                return (
                  <div
                    key={training.id}
                    className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {/* Calendar Badge */}
                    <div className="text-center min-w-0">
                      <div className="text-sm font-semibold text-gray-900">{calendarData.day}</div>
                      <div className="text-xs text-gray-500">{calendarData.month}</div>
                      <div className="text-xs text-gray-500">{calendarData.time}</div>
                    </div>

                    {/* Training Info */}
                    <div className="flex-1 ml-4 min-w-0">
                      <h4 className="text-sm font-medium text-gray-900 truncate">
                        {training.titel}
                      </h4>
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        {training.location && (
                          <>
                            <span>{training.location}</span>
                            <span>•</span>
                          </>
                        )}
                        <span>{training.anmalda || 0} närvarande</span>
                        {training.max_platser && (
                          <>
                            <span>/</span>
                            <span>{training.max_platser} anmälda</span>
                          </>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        {formatDateTime(training.datum)}
                      </div>
                    </div>

                    {/* Badges and Actions */}
                    <div className="flex items-center space-x-2 ml-4">
                      {/* Badges */}
                      <div className="flex space-x-1">
                        {training.bootcamp && (
                          <Badge variant="info">
                            {training.bootcamp_name ? `Bootcamp: ${training.bootcamp_name}` : 'Bootcamp'}
                          </Badge>
                        )}
                        {training.free_training && <Badge variant="success">Gratis</Badge>}
                      </div>

                      {/* Actions */}
                      <div className="flex space-x-2">
                        <Link to={`/training/${training.id}/attendance`}>
                                                  <Button variant="outline" size="sm" title="Redigera närvaro">
                          <PenSquare className="h-4 w-4" />
                        </Button>
                        </Link>
                        <Button variant="outline" size="sm" title="Ta bort från arkiv">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
